import {
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { OrderStatus } from "components/OrderStatus";
import { SubOrderPayments } from "components/SubOrderPayments";
import { SUB_ORDER_STATUS } from "data/consts";
import { DELIVERY_METHOD } from "data/i18n";
import { toDate, toFullDateName } from "helpers/dateHelper";
import { toCurrencyFormat } from "helpers/utils";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import withRouter from "hoc/withRouter";
import { useLoadAll } from "hooks/useLoadAll";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import subOrdersService from "services/admin/sub-orders.service";

interface SubOrderDetailsProps {
  router: {
    params: { id: string };
  };
  data: { subOrder: any };
}

const SubOrderDetails = ({
  router: {
    params: { id },
  },
  data: { subOrder },
}: SubOrderDetailsProps) => {
  const { t } = useTranslation();

  const acceptOrder = async () => {
    try {
      const { data } = await subOrdersService.acceptOrder(subOrder.id);
      toast.success(data.message);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error("acceptOrder error:", error);
      toast.error(`Во время принятия заказа вышла ошибка: ${error?.message}`);
    }
  };
  const cancelOrder = async () => {
    console.log("cancelOrder");
    const isConfirmed = window.confirm("Вы точно хотите отменить заказ?");
    if (!isConfirmed) {
      return;
    }

    try {
      const { data } = await subOrdersService.cancelOrder(subOrder.id);
      toast.success(data.message);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error("cancelOrder error:", error);
      toast.error(`Во время отмены заказа вышла ошибка: ${error?.message}`);
    }
  };

  const sendToDelivery = async () => {
    try {
      const { data } = await subOrdersService.sendToDelivery(subOrder.id);
      toast.success(data.message);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error("sendToDelivery error:", error);
      toast.error(`Во время передачи заказа в доставку вышла ошибка: ${error?.message}`);
    }
  };

  const buttons = () => {
    // Если заказ новый или оплачен частично, то можно принять заказ или отменить заказ
    const cancelButton = (
      <Button variant="outlined" color="error" onClick={cancelOrder}>
        Отменить заказ
      </Button>
    );
    const acceptButton = (
      <Button variant="contained" color="primary" onClick={acceptOrder}>
        Принять заказ
      </Button>
    );

    const sendToDeliveryButton = (
      <Button variant="contained" color="primary" onClick={sendToDelivery}>
        Передать в доставку
      </Button>
    );

    // Если заказ новый или оплачен частично, то можно принять заказ или отменить заказ
    if ([SUB_ORDER_STATUS.NEW, SUB_ORDER_STATUS.PAID].includes(subOrder.status)) {
      return (
        <div>
          {acceptButton} {cancelButton}
        </div>
      );
    }
    // Если заказ принят, то можно передать в доставку или отменить заказ
    if ([SUB_ORDER_STATUS.ACCEPTED].includes(subOrder.status)) {
      return (
        <>
          {sendToDeliveryButton} {cancelButton}
        </>
      );
    }

    // Если заказ передан в доставку, то можно отменить заказ
    if ([SUB_ORDER_STATUS.DELIVERY].includes(subOrder.status)) {
      return (
        <>
          {cancelButton}
        </>
      );
    }

    return null;
  };

  const updateSubOrderStatus = async (status: string) => {
    console.log("updateSubOrderStatus", status);
    try {
      const { data } = await subOrdersService.updateSubOrderStatus(subOrder.id, status);
      toast.success(data.message);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error("updateSubOrderStatus error:", error);
      toast.error(`Во время обновления статуса заказа вышла ошибка: ${error?.message}`);
    }
  };

  return (
    <div>
      <section
        className="m-b-16 flex ml-auto"
        style={{
          flexDirection: "row-reverse",
        }}
      >
        <Button variant="outlined" color="primary" onClick={() => window.history.back()}>
          Назад к списку заказов
        </Button>
      </section>
      <section className="m-b-16">
        <Card>
          <CardContent>
            <h3 className="m-b-8">Данные о заказе</h3>
            <div className="m-b-8">
              <strong>Номер заказа: </strong>
              {subOrder.id}
            </div>
            <div className="m-b-8">
              <strong>Дата создания: </strong>
              {toDate(subOrder.createdAt)}
            </div>
            <div className="m-b-8">
              <strong>Дата последнего изменения: </strong>
              {toDate(subOrder.updatedAt)}
            </div>
            <div className="m-b-8">
              <strong>Статус заказа: </strong>
              <OrderStatus status={subOrder.status} subStatus={subOrder.subStatus}></OrderStatus>
            </div>
          </CardContent>
        </Card>
      </section>
      <section className="m-b-16">
        <Card>
          <CardContent>
            <h3 className="m-b-16">Данные о покупателе</h3>
            <section className="flex gap-16">
              <div>
                <img
                  src="/media/images/avatar_m.jpg"
                  style={{ width: 100, height: 100, objectFit: "cover", borderRadius: "50%" }}
                />
              </div>
              <div>
                <div className="m-b-8">
                  <Link className="color-blue" to={`/admin/customers/view/${subOrder.customer.id}`}>
                    {subOrder.customer?.lastName} {subOrder.customer?.firstName}
                  </Link>
                </div>
                <div className="m-b-8">
                  <span>Номер телефона: </span>
                  <span>{_.get(subOrder, "customer.user.phone")}</span>
                </div>
                <div className="m-b-8">
                  <span>ИИН: </span>
                  <span>{_.get(subOrder, "customer.IDCard.iin")}</span>
                </div>
                <div className="m-b-8">
                  <span>Является клиентом: </span>
                  <span>{toFullDateName(_.get(subOrder, "customer.createdAt"))}</span>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      </section>
      <section className="m-b-16">
        <Card>
          <CardContent>
            <h3 className="m-b-8">Товары</h3>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Цена</TableCell>
                  <TableCell>Количество</TableCell>
                  <TableCell>Общая стоимость</TableCell>
                  <TableCell>Клиент</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subOrder.orderItems.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <div className="flex gap-8" style={{ alignItems: "center" }}>
                        <img
                          alt="product"
                          src={item.image}
                          style={{ width: 50, objectFit: "cover" }}
                        />
                        <Link className="color-blue" to={`/admin/products`}>
                          {item.name}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell>{toCurrencyFormat(item.price, subOrder.currency)}</TableCell>
                    <TableCell>{item.quantity} шт.</TableCell>
                    <TableCell>{toCurrencyFormat(item.subtotal, subOrder.currency)}</TableCell>
                    {item.customer ? (
                      <TableCell>
                        <Link
                          className="color-blue"
                          to={`/admin/customers/view/${item.customer.id}`}
                        >
                          {item.customer?.lastName} {item.customer?.firstName}
                        </Link>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>
      <section className="m-b-16">
        <Card>
          <CardContent>
            <h3 className="m-b-8">Способ доставки</h3>
            {subOrder.deliveryMethod ? (
              <div>
                <div>
                  Способ доставки:{" "}
                  {DELIVERY_METHOD[subOrder.deliveryMethod as keyof typeof DELIVERY_METHOD]}
                </div>
                <div>Дата доставки: {toFullDateName(subOrder.deliveryDateTo)}</div>
                <div>
                  Адрес доставки: {subOrder.deliveryAddress?.city?.name},{" "}
                  {subOrder.deliveryAddress?.address}, {subOrder.deliveryAddress?.appartmentNumber}
                </div>
              </div>
            ) : (
              <div>Способ доставки: не указан</div>
            )}
          </CardContent>
        </Card>
      </section>
      <SubOrderPayments subOrder={subOrder} onClose={() => {}} options={{}}></SubOrderPayments>
      <section className="m-b-32">
        <h3 className="m-b-8">Комментарий от покупателя</h3>
        <div>{subOrder.comment || "Комментарий отсутствует"}</div>
      </section>
      <section className="m-b-16 flex flex-col gap-16">
        <div>{buttons()}</div>
        <div>
          <span>Изменить статус заказа(только для тестирования/dev): </span>
          <select
            onChange={(e) => updateSubOrderStatus(e.target.value)}
            defaultValue={subOrder.status}
          >
            {Object.values(SUB_ORDER_STATUS).map((status: any) => (
              <option key={status} value={status}>
                {t(status)}
              </option>
            ))}
          </select>
        </div>
      </section>
    </div>
  );
};

export default withRouter(
  withRemoteDataAndSpinner(SubOrderDetails, (router: any) => {
    let requests: any = {};
    if (router.params.id) {
      requests["subOrder"] = () => subOrdersService.get(router.params.id);
    }
    return useLoadAll(requests);
  })
);
